import React, { useState, useEffect, useCallback } from 'react';
import { cldThumb } from '../utils/img';
import { getItemBrands } from '../api/inventory';
import { listImageReviewDesigns, markDesignsClean } from '../api/imageReview';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending review' },
  { value: 'clean',   label: 'Marked clean' },
  { value: 'all',     label: 'All' },
];
const PAGE_SIZE = 50;

export default function MarkImages() {
  const [brands, setBrands] = useState([]);
  const [brand, setBrand] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [page, setPage] = useState(0);
  const [designs, setDesigns] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    getItemBrands().then(d => setBrands(d.brands || [])).catch(() => {});
  }, []);

  const cleanParam = statusFilter === 'all' ? undefined : statusFilter === 'clean';

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await listImageReviewDesigns({
        brand_name: brand || undefined, clean: cleanParam, offset: page * PAGE_SIZE, limit: PAGE_SIZE,
      });
      setDesigns(data.designs);
      setTotal(data.total);
    } catch { setError('Failed to load designs.'); }
    finally { setLoading(false); }
  }, [brand, cleanParam, page]);

  // Filter changes reset to page 0; the page effect below then does the fetch.
  useEffect(() => { setPage(0); setSelected(new Set()); }, [brand, statusFilter]);
  useEffect(() => { setSelected(new Set()); load(); }, [load]);

  function toggleOne(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function selectAllVisible() {
    setSelected(new Set(designs.map(d => d.design_id)));
  }

  async function applyMark(clean) {
    if (selected.size === 0) return;
    setSaving(true); setError(null);
    try {
      await markDesignsClean([...selected], clean);
      setSelected(new Set());
      await load();
    } catch { setError('Failed to update.'); }
    finally { setSaving(false); }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min(total, page * PAGE_SIZE + designs.length);

  return (
    <div className="p-5">
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <select value={brand} onChange={e => setBrand(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:border-zoddle-green">
          <option value="">All brands</option>
          {brands.map(b => <option key={b} value={b}>{b}</option>)}
        </select>

        {STATUS_OPTIONS.map(o => (
          <button key={o.value} onClick={() => setStatusFilter(o.value)}
            className={`text-xs px-3 py-1.5 rounded-lg font-medium border transition-colors ${
              statusFilter === o.value ? 'bg-zoddle-green text-white border-zoddle-green' : 'bg-white text-gray-600 border-gray-200 hover:border-zoddle-green'
            }`}>
            {o.label}
          </button>
        ))}

        <p className="text-xs text-gray-400 ml-1">
          {loading ? 'Loading…' : total === 0 ? '0 designs' : `${rangeStart}–${rangeEnd} of ${total.toLocaleString()}`}
        </p>

        <div className="flex-1" />

        {designs.length > 0 && (
          <>
            <button onClick={selectAllVisible}
              className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 text-gray-600 hover:border-zoddle-blue transition-colors font-medium">
              Select all visible ({designs.length})
            </button>
            <span className="text-xs text-gray-400">{selected.size} selected</span>
            <button onClick={() => applyMark(true)} disabled={saving || selected.size === 0}
              className="text-xs bg-zoddle-green text-white rounded-lg px-3 py-1.5 font-semibold disabled:opacity-40">
              Mark selected clean
            </button>
            <button onClick={() => applyMark(false)} disabled={saving || selected.size === 0}
              className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 text-gray-600 font-semibold disabled:opacity-40">
              Mark selected not clean
            </button>
          </>
        )}
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-100 rounded-lg px-4 py-3 text-sm text-zoddle-pink">{error}</div>
      )}

      {!loading && designs.length === 0 && !error && (
        <p className="text-gray-400 text-sm">Nothing to show for this filter.</p>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
        {designs.map(d => {
          const isSelected = selected.has(d.design_id);
          return (
            <div key={d.design_id} onClick={() => toggleOne(d.design_id)}
              className={`border rounded-xl p-2 cursor-pointer transition ${
                isSelected ? 'border-zoddle-green ring-2 ring-zoddle-green bg-zoddle-green/5' : 'border-gray-100 hover:border-gray-300'
              }`}>
              {d.image_url_1
                ? <img src={cldThumb(d.image_url_1, 300)} alt={d.design_name || ''} loading="lazy"
                    className="w-full aspect-square object-cover rounded-lg bg-gray-100 mb-2" />
                : <div className="w-full aspect-square rounded-lg bg-gray-100 mb-2 flex items-center justify-center text-gray-300 text-xs">No image</div>}
              <p className="text-xs font-mono font-bold text-zoddle-blue truncate">Design {d.design_code}</p>
              <p className="text-xs text-gray-500 truncate">{d.design_name}</p>
              <p className="text-xs text-gray-400 truncate">{d.brand_name} · {d.gender} · {d.category}</p>
              {d.image_clean && <span className="inline-block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Clean</span>}
            </div>
          );
        })}
      </div>

      {total > 0 && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={loading || page === 0}
            className="text-xs border border-gray-200 rounded-lg px-4 py-1.5 text-gray-600 hover:border-zoddle-green transition-colors font-medium disabled:opacity-40">
            Previous
          </button>
          <span className="text-xs text-gray-400">Page {page + 1} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={loading || page >= totalPages - 1}
            className="text-xs border border-gray-200 rounded-lg px-4 py-1.5 text-gray-600 hover:border-zoddle-green transition-colors font-medium disabled:opacity-40">
            Next
          </button>
        </div>
      )}
    </div>
  );
}
